import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { invoiceService } from '../../services/invoiceService';
import { documentService } from '../../services/documentService';
import { DOCUMENT_TYPES } from '../../config';

function ViewInvoicePage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Credit Card',
    referenceNumber: '',
    notes: ''
  });
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [documentError, setDocumentError] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', customerName: '' });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!invoiceId) {
        setError('No Invoice ID specified.');
        setIsLoading(false);
        return;
      }

      if (!token) {
        setError('Authentication required. Please log in.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError('');
        
        const response = await invoiceService.getInvoiceById(invoiceId);
        
        if (response.status === 'success' && response.data?.invoice) {
          setInvoice(response.data.invoice);
        } else {
          setError('Invalid response format from server.');
        }
      } catch (err) {
        console.error('Error fetching invoice:', err);
        setError(err.message || 'Failed to fetch invoice details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceId, token]);

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      setError('Please enter a valid payment amount.');
      return;
    }

    setIsRecordingPayment(true);
    setError('');

    try {
      const response = await invoiceService.recordPayment(invoiceId, {
        amount: parseFloat(paymentData.amount),
        paymentDate: paymentData.paymentDate,
        paymentMethod: paymentData.paymentMethod,
        referenceNumber: paymentData.referenceNumber,
        notes: paymentData.notes
      });

      if (response.status === 'success' && response.data?.invoice) {
        setInvoice(response.data.invoice);
        setShowPaymentModal(false);
        setPaymentData({
          amount: '',
          paymentDate: new Date().toISOString().split('T')[0],
          paymentMethod: 'Credit Card',
          referenceNumber: '',
          notes: ''
        });
      } else {
        setError('Failed to record payment. Please try again.');
      }
    } catch (err) {
      console.error('Error recording payment:', err);
      setError(err.message || 'Failed to record payment.');
    } finally {
      setIsRecordingPayment(false);
    }
  };

  // Document handling functions
  const handleDownloadPdf = async () => {
    setIsGeneratingDocument(true);
    setDocumentError('');
    try {
      await documentService.downloadPdfFile(DOCUMENT_TYPES.INVOICE, invoiceId, `Invoice-${invoice.invoiceNumber || invoiceId}.pdf`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setDocumentError('Failed to download PDF. Please try again.');
    } finally {
      setIsGeneratingDocument(false);
      setShowPrintMenu(false);
    }
  };

  const handleViewPdf = () => {
    try {
      documentService.viewPdfInNewWindow(DOCUMENT_TYPES.INVOICE, invoiceId);
      setShowPrintMenu(false);
    } catch (error) {
      console.error('Error viewing PDF:', error);
      setDocumentError('Failed to open PDF viewer. Please try again.');
    }
  };

  const handlePreviewHtml = async () => {
    setIsGeneratingDocument(true);
    setDocumentError('');
    try {
      await documentService.previewInPopup(DOCUMENT_TYPES.INVOICE, invoiceId);
    } catch (error) {
      console.error('Error previewing HTML:', error);
      setDocumentError('Failed to generate HTML preview. Please try again.');
    } finally {
      setIsGeneratingDocument(false);
      setShowPrintMenu(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setIsSendingEmail(true);
    setDocumentError('');
    setEmailSuccess('');
    
    try {
      await documentService.sendEmail(
        DOCUMENT_TYPES.INVOICE,
        invoiceId,
        emailData.email || invoice.client?.email,
        emailData.customerName || invoice.client?.username
      );
      setEmailSuccess('Invoice sent successfully!');
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailSuccess('');
        setEmailData({ email: '', customerName: '' });
      }, 2000);
    } catch (error) {
      console.error('Error sending email:', error);
      setDocumentError(error.message || 'Failed to send email. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (isLoading) {
    return <div className="container mx-auto px-4 py-8 text-center"><p>Loading invoice details for #{invoiceId}...</p></div>;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Link to="/admin/invoices" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back to Invoices</Link>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Link to="/admin/invoices" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back to Invoices</Link>
        <p>No invoice data found for #{invoiceId}.</p>
      </div>
    );
  }
  
  const balanceDue = parseFloat(invoice.totalAmount || 0) - parseFloat(invoice.amountPaid || 0);
  const canBeEdited = invoice.status === 'Draft' || invoice.status === 'Pending';
  // Add logic for recording payment if needed

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-brand-text-dark">Invoice Details: {invoice.invoiceNumber || `#${invoice.id}`}</h1>
          <Link to="/admin/invoices" className="text-sm text-blue-600 hover:text-blue-800">&larr; Back to All Invoices</Link>
        </div>
      </header>

      <div className="bg-white shadow-lg rounded-lg p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6 pb-4 border-b">
          <div>
            <h2 className="text-sm font-medium text-gray-500">Billed To</h2>
            <p className="mt-1 text-lg text-gray-900">{invoice.client?.username || 'N/A'}</p>
            {invoice.client?.email && <p className="text-xs text-gray-600">{invoice.client.email}</p>}
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Invoice Date</h2>
            <p className="mt-1 text-lg text-gray-900">{formatDate(invoice.invoiceDate)}</p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Due Date</h2>
            <p className="mt-1 text-lg text-gray-900">{formatDate(invoice.dueDate)}</p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Status</h2>
            <p className="mt-1 text-lg">
              <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${ 
                invoice.status === 'Draft' ? 'bg-gray-100 text-gray-800' :
                invoice.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                invoice.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                invoice.status === 'Void' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {invoice.status}
              </span>
            </p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Amount Paid</h2>
            <p className="mt-1 text-lg text-gray-900">${parseFloat(invoice.amountPaid || 0).toFixed(2)}</p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Balance Due</h2>
            <p className={`mt-1 text-lg font-semibold ${balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
              ${balanceDue.toFixed(2)}
            </p>
          </div>
        </div>

        {invoice.lineItems && invoice.lineItems.length > 0 ? (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Line Items</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoice.lineItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{item.description}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-right">{item.quantity}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-right">${parseFloat(item.unitPrice || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 font-semibold text-right">${parseFloat(item.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : <p className="text-gray-500 mb-6">No line items found for this invoice.</p> }

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 border-t pt-6">
          <div>
            <p className="text-sm text-gray-500">Subtotal:</p>
            <p className="text-lg text-gray-700 font-semibold text-right">${parseFloat(invoice.subTotal || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Tax ({ ((invoice.taxRate || 0) * 100).toFixed(2) }%):</p>
            <p className="text-lg text-gray-700 font-semibold text-right">${parseFloat(invoice.taxAmount || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Amount:</p>
            <p className="text-xl text-gray-800 font-bold text-right">${parseFloat(invoice.totalAmount || 0).toFixed(2)}</p>
          </div>
        </div>

        {invoice.notes && (
          <div className="mb-6 pt-4 border-t">
            <h4 className="text-md font-semibold text-gray-700 mb-1">Notes:</h4>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{invoice.notes}</p>
          </div>
        )}
        {invoice.termsAndConditions && (
          <div className="mb-6 pt-4 border-t">
            <h4 className="text-md font-semibold text-gray-700 mb-1">Terms & Conditions:</h4>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{invoice.termsAndConditions}</p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap justify-end space-x-0 sm:space-x-3 space-y-3 sm:space-y-0">
          <div className="relative inline-block">
            <button 
              onClick={() => setShowPrintMenu(!showPrintMenu)} 
              className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out flex items-center"
              disabled={isGeneratingDocument}
            >
              {isGeneratingDocument ? 'Processing...' : 'Print Invoice'}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            
            {showPrintMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                <div className="py-1">
                  <button 
                    onClick={handleDownloadPdf}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Download PDF
                  </button>
                  <button 
                    onClick={handleViewPdf}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    View PDF in Browser
                  </button>
                  <button 
                    onClick={handlePreviewHtml}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Preview HTML
                  </button>
                  <button 
                    onClick={() => window.print()}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Print Current Page
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setShowEmailModal(true)}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
          >
            Send Email
          </button>
          
          {documentError && (
            <div className="w-full mt-2 text-red-600 text-sm">
              {documentError}
            </div>
          )}
          
          {canBeEdited && (
            <button 
              onClick={() => navigate(`/admin/invoices/edit/${invoice.id}`)} 
              className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
            >
              Edit Invoice
            </button>
          )}
          
          {invoice.status === 'Pending' && balanceDue > 0 && ['admin', 'manager', 'staff'].includes(user?.role) && (
            <button 
              onClick={() => setShowPaymentModal(true)}
              className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
            >
              Record Payment
            </button>
          )}
        </div>
      </div>

      {/* Payment Recording Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Record Payment</h3>
              <form onSubmit={handleRecordPayment}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={balanceDue}
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Max: $${balanceDue.toFixed(2)}`}
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    value={paymentData.paymentDate}
                    onChange={(e) => setPaymentData({...paymentData, paymentDate: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData({...paymentData, paymentMethod: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Credit Card">Credit Card</option>
                    <option value="Cash">Cash</option>
                    <option value="Check">Check</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reference Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={paymentData.referenceNumber}
                    onChange={(e) => setPaymentData({...paymentData, referenceNumber: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Transaction ID, Check number, etc."
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Additional payment notes..."
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition duration-150 ease-in-out"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isRecordingPayment}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-md transition duration-150 ease-in-out"
                  >
                    {isRecordingPayment ? 'Recording...' : 'Record Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send Invoice via Email</h3>
              <form onSubmit={handleSendEmail}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={emailData.email || invoice?.client?.email || ''}
                    onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="customer@example.com"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name (optional)
                  </label>
                  <input
                    type="text"
                    value={emailData.customerName || invoice?.client?.username || ''}
                    onChange={(e) => setEmailData({ ...emailData, customerName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Customer Name"
                  />
                </div>
                {emailSuccess && (
                  <div className="mb-4 p-2 bg-green-100 text-green-700 rounded">
                    {emailSuccess}
                  </div>
                )}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmailData({ email: '', customerName: '' });
                      setDocumentError('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition duration-150 ease-in-out"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSendingEmail}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition duration-150 ease-in-out"
                  >
                    {isSendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewInvoicePage;